import { BOOLEAN_SELECT_LIST_ITEMS, Box, CancelIconButton, Grid, Stack, Tooltip, Typography } from "@compass/core";
import InvalidIcon from "@mui/icons-material/Block";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { DatePicker, Input, ReportPropertyIcon, Select, TimePicker } from "components";
import { CustomFilterEntity, CustomFilter as CustomFilterModel, CustomReportProperty, ReportPropertyBase, ReportPropertyType } from "generated/openApi";
import produce from "immer";
import { useEffect, useState } from "react";
import { CustomFilterOperatorOption, customFilterOperatorOptions } from "./operators";
import { getOperatorOptions, validateCustomFilter } from "./services";

export type CustomEntityOption = {
  EntityType: CustomFilterEntity,
  EntityName: string
};

type DisplayOperatorOption = CustomFilterOperatorOption & {
  logic?: string;
};

const NOT_EXISTS_LOGIC = "NotExists";

const notExistsOperatorOption: DisplayOperatorOption = {
  ...customFilterOperatorOptions[0],
  description: "does not have",
  logic: NOT_EXISTS_LOGIC,
};

const isNotExistsLogic = (logic?: string) => {
  if (!logic) return false;

  const normalizedLogic = logic.replaceAll(" ", "").replaceAll("_", "").replaceAll("-", "");

  return ["notexists", "doesnotexist", "not", "no", "without", "missing", "absent", "lacks"].includes(
    normalizedLogic.toLowerCase()
  );
};

export const CustomFilter = (props: {
  filter: CustomFilterModel;
  setFilter: (filter: CustomFilterModel) => void;
  property: CustomReportProperty | undefined;
  removeFilter: () => void;
  allowedEntityTypes: CustomEntityOption[];
}) => {
  const selectedProperty = props.filter.property;
  const selectedOperatorOption = customFilterOperatorOptions?.find((x) => x.operator === props.filter.operator);
  const baseOperatorOptions = getOperatorOptions(selectedProperty) ?? [];
  const supportsNotExistsLogic =
    props.filter.entity === CustomFilterEntity.StudentGrades ||
    props.filter.entity === CustomFilterEntity.StudentService;
  const selectableOperatorOptions: DisplayOperatorOption[] = supportsNotExistsLogic
    ? [notExistsOperatorOption, ...baseOperatorOptions]
    : baseOperatorOptions;
  const displayOperatorOption = supportsNotExistsLogic && isNotExistsLogic(props.filter.logic)
    ? notExistsOperatorOption
    : selectableOperatorOptions.find((x) => x.operator === props.filter.operator && !x.logic);

  const [inputValue, setInputValue] = useState<string>("");

  useEffect(() => {
    props.setFilter(
      produce(props.filter, (draft) => {
        const errorMessage = validateCustomFilter(props.filter, customFilterOperatorOptions);
        draft.isValid = !errorMessage;
      })
    );
  }, [props.filter.entity, props.filter.property, props.filter.operator, props.filter.values]);

  const renderValueBox = () => {
    const selectedPropertyType = selectedProperty?.propertyType;
    const hasSelectList = selectedProperty?.selectList && selectedProperty?.selectList?.length > 0;

    if (hasSelectList) {
      return (
        <Select
          multiple
          label={"Multiple Values"}
          value={selectedProperty?.selectList?.filter((x) => props.filter?.values?.includes(x.id.toString()))}
          getOptionLabel={(option) => option.name}
          onChange={(e, values) => {
            props.setFilter(
              produce(props.filter, (draft) => {
                draft.values = values.map((x) => x.id.toString());
              })
            );
          }}
          options={selectedProperty?.selectList}
          groupBy={(option) => option.group}
          placeholder="Select values"
          size="small"
        />
      );
    }

    if (
      selectedPropertyType === ReportPropertyType.String ||
      selectedPropertyType === ReportPropertyType.Int ||
      selectedPropertyType === ReportPropertyType.Decimal
    ) {
      if (selectedOperatorOption?.filterValueType === "Multiple")
        return (
          <Select
            multiple
            freeSolo
            label={"Multiple Values"}
            inputValue={inputValue}
            value={props.filter?.values}
            getOptionLabel={(option) => option || ""}
            onChange={(e, values) => {
              props.setFilter(
                produce(props.filter, (draft) => {
                  draft.values = values;
                })
              );
              setInputValue("");
            }}
            onInputChange={(e) => setInputValue(e.target.value)}
            options={
              selectedOperatorOption.canUseSelectList ? selectedProperty?.selectList?.map((x) => x.name) : undefined
            }
            onBlur={() => {
              props.setFilter(
                produce(props.filter, (draft) => {
                  if (inputValue) {
                    if (!draft.values) draft.values = [];
                    draft.values.push(inputValue);
                  }
                })
              );
              setInputValue("");
            }}
            placeholder="Type each value and press enter"
            size="small"
          />
        );

      if (selectedOperatorOption?.filterValueType === "Single")
        return (
          <Input
            label={"Value"}
            value={props.filter?.values?.[0]}
            onChange={(e) => {
              props.setFilter(
                produce(props.filter, (draft) => {
                  draft.values = e.target.value ? [e.target.value] : [];
                })
              );
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                // prevent enter from submitting the form
                event.preventDefault();
              }
            }}
            size="small"
          />
        );
    }

    if (selectedPropertyType === ReportPropertyType.Date || selectedPropertyType === ReportPropertyType.DateTimeOffset)
      return (
        <DatePicker
          label={"Value"}
          value={props.filter?.values?.[0]}
          onChange={(value) =>
            props.setFilter(
              produce(props.filter, (draft) => {
                draft.values = value ? [value] : [];
              })
            )
          }
          size="small"
        />
      );

    if (selectedPropertyType === ReportPropertyType.Bool || selectedPropertyType === ReportPropertyType.NullableBool)
      return (
        <Select
          label={"Value"}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id.toString() === props.filter?.values?.[0])}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => {
            props.setFilter(
              produce(props.filter, (draft) => {
                draft.values = [value.id.toString()];
              })
            );
          }}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          size="small"
        />
      );

    if (selectedPropertyType === ReportPropertyType.TimeSpan)
      return (
        <TimePicker
          label="Value"
          value={props.filter?.values?.[0]}
          onChange={(value) =>
            props.setFilter(
              produce(props.filter, (draft) => {
                draft.values = [value];
              })
            )
          }
          size="small"
        />
      );

    return (
      <Input
        label={"Value"}
        disabled
        size="small"
      />
    );
  };

  return (
    <Box
      sx={{
        borderRadius: 2,
        px: 1.5,
        py: 2,
        width: "100%",
        // boxShadow: 1,
        border: "1px solid",
        borderColor: "divider",
      }}
    >

      <Grid container alignItems="center" wrap="nowrap">
        <Grid item xs={3}>
          <Select
            label="Entity"
            value={props.allowedEntityTypes.find(
              o => o.EntityType === props.filter.entity
            )}
            options={props.allowedEntityTypes}
            getOptionLabel={(option) => option.EntityName}
            onChange={(e, value) =>
              props.setFilter(
                produce(props.filter, (draft) => {
                  draft.entity = value.EntityType;
                  draft.values = [];
                  draft.logic = undefined;
                })
              )
            } size="small"
            sx={{
              '& .MuiAutocomplete-clearIndicator': {
                display: 'none',
              },
            }}
          />
        </Grid>

        <Grid item xs={3}>
          <Select
            label="Field"
            value={selectedProperty}
            options={props.property?.customProperties}
            getOptionLabel={(option) => option.displayName!}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Stack direction="row" spacing="sm" alignItems="center">
                  <ReportPropertyIcon property={option} />
                  <Typography>{option.displayName}</Typography>
                </Stack>
              </Box>
            )}
            onChange={(e, value) =>
              props.setFilter(
                produce(props.filter, (draft) => {
                  draft.property = value;
                  draft.propertyType =
                    value.propertyType ?? ReportPropertyType.String;
                  draft.values = [];
                })
              )
            } size="small"
            sx={{
              '& .MuiAutocomplete-clearIndicator': {
                display: 'none',
              },
            }}
          />
        </Grid>

        <Grid item xs={2}>
          <Select
            label="Operator"
            value={displayOperatorOption}
            options={selectableOperatorOptions}
            getOptionLabel={(option) => option.description!}
            onChange={(e, value) =>
              props.setFilter(
                produce(props.filter, (draft) => {
                  draft.operator = value.operator;
                  draft.logic = value.logic;
                })
              )
            } size="small"
            isOptionEqualToValue={(option, value) =>
              option.operator === value.operator && (option.logic ?? "") === (value.logic ?? "")
            }
            sx={{
              '& .MuiAutocomplete-clearIndicator': {
                display: 'none',
              },
            }}
          />
        </Grid>

        <Grid item xs={4}>
          {selectedOperatorOption?.filterValueType !== "None" &&
            renderValueBox()}
        </Grid>

        <Grid item xs="auto">
          <CancelIconButton color="primary" onClick={props.removeFilter} />
        </Grid>
      </Grid>
    </Box>

  );
};
